import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AutoCompleteDomains, ButtonBaseType, ID, Maybe } from 'src/models';
import { useAutocomplete } from 'src/hooks/useAutocomplete';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { getAppParams } from 'src/store/app/app.getters';
import { ifTrue } from 'src/tools/logic.tools';
import { P2 } from 'src/components/Typography';

export function TradesAutoComplete() {
  const {
    onChange,
    classNames,
    onSubmit = mockFn,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    onClick,
    customization = {},
    actions = {},
    placeholder,
  } = useContext(InputContext);

  const companyIdRef = useRef<Maybe<ID>>();

  const {
    isInline,
    isSmall,
    isXSmall,
    isSmallMenu,
    companyId,
    dropdownMinWidth = 'container-width',
    leftIconId,
    isWithCompanyName,
    position,
  } = customization;

  const isTradesNavbar = !companyId;

  const domain = isTradesNavbar ? AutoCompleteDomains.TRADES_NAVBAR : AutoCompleteDomains.PROJ_COMPANY_TRADES;

  const { onChangeInput } = actions;

  const [inputValue, setInputValue] = useState<Maybe<string>>();

  const { subId } = useSelector(getAppParams);

  const ids = useMemo(() => (companyId ? [subId, companyId] : [subId]) as string[], [companyId, subId]);

  const [isOpen, setIsOpen] = useState(false);

  const { results, isLoading, onChangeHandler } = useAutocomplete(domain, ids);

  const handleInputOnChange = useCallback(
    (userInput: string, isInitial?: boolean) => {
      setIsOpen(true);
      onChange(undefined);
      setInputValue(userInput);
      onChangeHandler(userInput, ifTrue(isInitial, 0));

      if (onChangeInput) onChangeInput(userInput);
    },
    [onChange, onChangeHandler, onChangeInput],
  );

  const handleOnItemClick = useCallback(
    (id: ID) => {
      const item = results.find(i => i.id === id);

      const label = companyId ? item?.label : item?.sub_label;

      onChange({ id, label, sub_label: item?.sub_label, icon_url: item?.icon_url }, true);

      if (onChangeInput) onChangeInput(label);

      setInputValue(label);

      setIsOpen(false);
    },
    [companyId, onChange, onChangeInput, results],
  );

  useEffect(() => {
    if (companyId !== companyIdRef.current) {
      companyIdRef.current = companyId;

      handleInputOnChange('', !!companyId);
    }
  }, [companyId, handleInputOnChange]);

  useEffect(() => {
    if (companyId && !inputValue && results.length) handleOnItemClick(results[0].id);
  }, [companyId, inputValue, results, handleOnItemClick]);

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function handleOnSubmit() {
    setIsOpen(false);

    onSubmit();
  }

  function renderDropdownLabel({ label, sub_label }: ButtonBaseType) {
    if (isTradesNavbar && isWithCompanyName)
      return (
        <P2 title={`${sub_label} | ${label}`}>
          <em>{sub_label}</em>
          {label}
        </P2>
      );

    return (companyId ? label : sub_label) ?? '';
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const iconId = baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        onChange={handleInputOnChange}
        onDiscard={handleOnDiscard}
        onSubmit={handleOnSubmit}
        onClick={getHandleOnClick(baseComponentProps.onClick)}
        isLoading={isLoading}
        value={inputValue}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        leftIconId={leftIconId}
        rightIconId={ifTrue(results.length, iconId)}
        isSmall={!!isSmall}
        isXSmall={!!isXSmall}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={results}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen && !isLoading}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderItemLabel={renderDropdownLabel}
      isSmall={!!isSmallMenu}
      isInline={!!isInline}
      dropdownMinWidth={dropdownMinWidth}
      position={position}
    />
  );
}
